import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import {
  CalendarInfoTask,
  LinkedTasks,
  PlanChipType,
  Task,
  TaskType,
  CalendarEventInterface,
  SubtaskForm,
} from '@lyceo/shared/plan/util-models';
import {
  addMinutes,
  compareAsc,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
} from 'date-fns';
import { defaultColor, subtaskColor } from './constants';

/**
 * Default sorter. Main tasks are at the top, followed by subtasks. If the
 * sortDoneAt is set to true, the tasks that are finished are sorted similarly,
 * but below unfinished tasks. For both sets, the position is used to sort them
 * after.
 */
export function sortTasks(tasks: Task[], sortDoneAt: boolean = true): Task[] {
  return [...tasks].sort((a, b) => {
    if (sortDoneAt) {
      const aDone: boolean = isTaskDone(a);
      const bDone: boolean = isTaskDone(b);
      if (!aDone && bDone) {
        return -1;
      } else if (aDone && !bDone) {
        return 1;
      }
    }

    const aSub: boolean = !!a.parentTaskId;
    const bSub: boolean = !!b.parentTaskId;

    if (!aSub && bSub) {
      return -1;
    } else if (aSub && !bSub) {
      return 1;
    }

    return a.position - b.position;
  });
}

/**
 * For the Task Modal, sorts all tasks (finished or unfinished) by date and
 * position and ignores finished status.
 */
export function sortTasksByDateAndPosition(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (isSameDay(a.scheduledAt, b.scheduledAt)) {
      return a.position - b.position;
    }

    return compareAsc(a.scheduledAt, b.scheduledAt);
  });
}

export function calendarInfoTaskToEvent(
  calendarInfoTask: CalendarInfoTask,
): CalendarEventInterface {
  return {
    title: calendarInfoTask.task.course?.name ?? '',
    linkId: calendarInfoTask.task.id,
    linkList: calendarInfoTask.linkedTasks,
    priority: calendarInfoTask.priority,
    finished: calendarInfoTask.finished,
    resizable: {
      beforeStart: true,
      afterEnd: true,
    },
    meta: {
      task: calendarInfoTask.task,
    },
    range: {
      start: new Date(calendarInfoTask.task.scheduledAt),
      end: new Date(calendarInfoTask.task.scheduledAt),
      startInclusive: false,
      endInclusive: false,
    },
    color: getSubOrMainTaskColor(calendarInfoTask.task),
    draggable: !calendarInfoTask.task.elo,
    reorder: !!calendarInfoTask.task.elo,
    type: taskToChipType(calendarInfoTask.task),
  };
}

export function getMainTaskColor(
  type: TaskType,
): CalendarEventInterface['color'] {
  switch (type) {
    case TaskType.EXAM:
      return {
        color: '--trix-color-plan-exam-dark',
        contrast: '--trix-color-plan-contrast',
      };
    case TaskType.HAND_IN:
      return {
        color: '--trix-color-plan-hand-in-dark',
        contrast: '--trix-color-plan-contrast',
      };
    case TaskType.HOMEWORK:
      return {
        color: '--trix-color-plan-homework-dark',
        contrast: '--trix-color-plan-contrast',
      };
    case TaskType.APPOINTMENT:
      return {
        color: '--trix-color-plan-appointment-dark',
        contrast: '--trix-color-plan-contrast',
      };
    default:
      return defaultColor;
  }
}

export function getSubOrMainTaskColor(
  task: Task,
): CalendarEventInterface['color'] {
  if (task.parentTaskId) {
    return subtaskColor;
  }

  return getMainTaskColor(task.type);
}

// eslint-disable-next-line complexity,max-lines-per-function
export function getNewPosition(
  index: number,
  task: Task,
  tasks: Task[],
): number {
  const total: number = tasks.length;
  const isTaskMain: boolean = !task.parentTaskId;

  if ((isTaskMain && index === 0) || tasks.length === 0) {
    // Main tasks can always be put at the top.
    return 0;
  }

  const atIndex: Task = tasks[index < total ? index : total - 1];
  const isAtIndexMain: boolean = !atIndex.parentTaskId;

  if (index >= total - 1 && !isTaskMain) {
    // Subtasks are always allowed at the bottom.
    return atIndex.position + 1;
  }

  if (isTaskMain && !isAtIndexMain) {
    // Find the first main task in the reversed list (i.e. the lowest position).
    for (const check of tasks.reverse()) {
      if (!check.parentTaskId && check.id !== task.id) {
        return check.position + 1;
      }
    }
    return 0;
  }

  if (!isTaskMain && isAtIndexMain) {
    // If the task is a subtask and the desired position is a main task, the
    // next best thing will be the first subtask it can find. Starting from the
    // desired index, slice it and join the tasks array so a new list is created
    // and searched through.
    const fromIndex: Task[] = tasks.slice(index, tasks.length);
    const toIndex: Task[] = tasks.slice(0, index);
    const joined: Task[] = fromIndex.concat(toIndex);
    // Check forward to get the first position of a subtask and replace that.
    for (const check of joined) {
      if (check.parentTaskId && check.id !== task.id) {
        return check.position;
      }
    }
    // Find the last item in the list and return the position + 1.
    const last: Task = tasks[tasks.length - 1];
    return last.position + 1;
  }
  // Replace the current task's position!
  return atIndex.position;
}

export function isTaskDone(task: Task): boolean {
  return !!task.doneAt || !!task.pupilDoneAt;
}

export function isSubtask(task: Task): boolean {
  return !!task.parentTaskId;
}

export function taskToChipType(task: Task): PlanChipType | undefined {
  if (isSubtask(task)) {
    return PlanChipType.SUBTASK;
  }
  switch (task.type) {
    case TaskType.EXAM:
      return PlanChipType.EXAM;
    case TaskType.HAND_IN:
      return PlanChipType.HAND_IN;
    case TaskType.HOMEWORK:
      return PlanChipType.HOMEWORK;
    case TaskType.APPOINTMENT:
      return PlanChipType.APPOINTMENT;
    default:
      return undefined;
  }
}

export function getLinkedTasks(tasks: Task[]): LinkedTasks {
  return tasks.reduce((linkedTasks: LinkedTasks, task) => {
    const filterId: string = getParentTaskId(task);

    linkedTasks[task.id] = tasks
      .filter(
        (filterTask) =>
          filterTask.parentTaskId?.includes(filterId) ||
          filterTask.id === filterId,
      )
      .map((mapTask) => mapTask.id);

    return linkedTasks;
  }, {});
}

/**
 * Subtasks have their parentTaskId filled, but that's an IRI. So, to get the
 * correct uuid, the string is split and the last part is retrieved. This works
 * for both urls that start with `/api/tasks/{task id}` or urls that have the
 * pupil prepended: `/api/pupils/{pupil id}/tasks/{task id}`.
 */
export function getParentTaskId(task: Task): string {
  if (task.parentTaskId) {
    const parts: string[] = task.parentTaskId.split('/');
    return parts[parts.length - 1];
  }
  return task.id;
}

export function getNextIndex(date: Date, tasks: SubtaskForm[]): number {
  if (tasks.length === 0 || isAfter(tasks[0].scheduledAt, date)) {
    return 0;
  }
  // Keep going until the next task is scheduled after the date param.
  let index: number = 0;
  while (
    isBefore(tasks[index].scheduledAt, date) ||
    isSameDay(tasks[index].scheduledAt, date)
  ) {
    index += 1;
    if (index === tasks.length) {
      break;
    }
  }

  return index;
}

// eslint-disable-next-line max-lines-per-function
export function dragSortPredicate(
  index: number,
  drag: CdkDrag<CalendarEventInterface<CalendarInfoTask>>,
  dropList: CdkDropList<CalendarEventInterface<CalendarInfoTask>[]>,
): boolean {
  if (!dropList.data.length) {
    // List is empty, so yeah, go ahead.
    return true;
  }

  const isParent: boolean = drag.data.meta?.task?.parentTaskId === undefined;
  const target: CalendarEventInterface = dropList.data[index];
  const targetParent: boolean = target?.meta?.task?.parentTaskId === undefined;

  if (isParent) {
    // Always allow at the start of the list.
    if (index === 0) {
      return true;
    }
    const previous: CalendarEventInterface = dropList.data[index - 1];
    // No target (i.e. end of list), make sure previous one is not subtask!
    if (!target) {
      return previous?.meta?.task?.parentTaskId === undefined;
    }
    // If the target drop container is different, allow for the parent item to
    // be placed below the parent items in the new container.
    if (drag.dropContainer !== dropList && previous && !targetParent) {
      return previous.meta?.task?.parentTaskId === undefined;
    }
    return targetParent;
  }

  if (!target) {
    // No item at the end of the list and a subtask? Go for it.
    return true;
  }
  // Check if the target is parent. Subtasks can only be placed below, so the
  // target can't move down if it's a parent task.
  return !targetParent;
}

/**
 * Get the minutes for a time string.
 * The time should be in the format HH:mm:ss.
 */
export function getMinutesByTimeString(time: string): number {
  const start: Date = new Date('1970-01-01 00:00:00');
  const end: Date = new Date('1970-01-01 ' + time);

  return differenceInMinutes(end, start);
}

export function getTimeStringByMinutes(minutes: string | number): string {
  const date: Date = addMinutes(new Date('1970-01-01 00:00:00'), +minutes);

  return format(date, 'HH:mm:ss');
}
